import { strFromU8, unzipSync } from 'fflate'

import type { ModelKey } from '../types/modelManagement'

export interface ModelPackageLabel {
    id: number
    en: string
    zh: string
}

export interface ParsedModelPackage {
    modelKey: ModelKey
    versionLabel: string
    modelFile: File
    labels: ModelPackageLabel[]
}

const VERSION_LABEL = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,127}$/

const fail = (message: string): never => {
    throw new Error(message)
}

export const parseModelPackage = async (
    packageFile: File,
): Promise<ParsedModelPackage> => {
    if (!packageFile.name.toLowerCase().endsWith('.zip')) {
        return fail('模型包必须是 ZIP 文件')
    }
    const bytes = packageFile.arrayBuffer
        ? await packageFile.arrayBuffer()
        : await new Response(packageFile).arrayBuffer()
    const entries = unzipSync(new Uint8Array(bytes))
    const names = Object.keys(entries)
    const modelNames = names.filter((name) => name.toLowerCase().endsWith('.pt'))
    if (
        names.length !== 2 ||
        modelNames.length !== 1 ||
        !names.includes('model-labels.json')
    ) {
        return fail('模型包必须包含一个 PT 文件和 model-labels.json')
    }
    const modelName = modelNames[0]
    const labelsPayload = JSON.parse(
        strFromU8(entries['model-labels.json']),
    ) as { model_name?: unknown; model_version?: unknown; labels?: unknown }
    if (!['mosquito', 'insect'].includes(String(labelsPayload.model_name))) {
        return fail('model_name 必须是 mosquito 或 insect')
    }
    const modelKey = labelsPayload.model_name as ModelKey
    if (
        typeof labelsPayload.model_version !== 'string' ||
        !VERSION_LABEL.test(labelsPayload.model_version.trim())
    ) {
        return fail('model_version 格式不正确')
    }
    if (!Array.isArray(labelsPayload.labels)) {
        return fail('model-labels.json 缺少 labels')
    }
    const labels = labelsPayload.labels.map((item, index) => {
        if (!item || typeof item !== 'object') {
            return fail(`模型识别类型 ${index} 格式不正确`)
        }
        const value = item as Record<string, unknown>
        if (
            typeof value.id !== 'number' ||
            !Number.isInteger(value.id) ||
            value.id < 0 ||
            typeof value.en !== 'string' ||
            typeof value.zh !== 'string' ||
            !value.en.trim() ||
            !value.zh.trim()
        ) {
            return fail(`模型识别类型 ${index} 缺少有效的 en/zh`)
        }
        return { id: value.id, en: value.en.trim(), zh: value.zh.trim() }
    })
    const modelFile = new File([entries[modelName]], modelName, {
        type: 'application/octet-stream',
    })
    return {
        modelKey,
        versionLabel: labelsPayload.model_version.trim(),
        modelFile,
        labels,
    }
}
